import { types, Instance } from "mobx-state-tree";
import { categoryIconMap, universalSearchResultItem } from "./UniversalSearchStore";
import { FluentIcon, Search24Filled } from "@fluentui/react-icons";
import { toJS } from "mobx";

export const maxShownHistoryCount = 5;
const maxHistoryCount = 50;

const SearchResultItemModel = types.model("SearchResultItem", {
    id: types.string,
    title: types.string,
    description: types.maybe(types.string),
    icon: types.maybe(types.frozen<FluentIcon>()),
    appPath: types.maybe(types.string),
    navigationTarget: types.maybe(types.string),
    category: types.maybe(types.string),
    originalCategory: types.maybe(types.string),
    timestamp: types.optional(types.number, () => Date.now())
});

export const UserSearchSettings = types
    .model("UserSearchSettings", {
        categoryToIds: types.map(types.array(SearchResultItemModel)),
        version: types.optional(types.number, 0)
    })
    .views((self) => ({
        getAllItems(): Instance<typeof SearchResultItemModel>[] {
            const allItems: Instance<typeof SearchResultItemModel>[] = [];

            Array.from(self.categoryToIds.keys()).forEach((category) => {
                const items = self.categoryToIds.get(category) || [];
                items.forEach((item) => {
                    allItems.push({
                        id: item.id,
                        title: item.title,
                        description: item.description,
                        icon: toJS(categoryIconMap[(item.originalCategory || item.category)?.toLowerCase() ?? ""]) ?? toJS(Search24Filled),
                        appPath: item.appPath,
                        category: "Recent Searches",
                        originalCategory: item.originalCategory,
                        navigationTarget: item.navigationTarget,
                        timestamp: item.timestamp
                    });
                });
            });

            return allItems.sort((a, b) => b.timestamp - a.timestamp);
        },
        findItem(category: string, itemId: string): boolean {
            const items = self.categoryToIds.get(category) || [];

            if (items.some((item) => item.id === itemId)) {
                return true;
            }
            return false;
        }
    }))
    .actions((self) => ({
        clearSearchHistory() {
            self.categoryToIds.clear();
            self.version += 1;
        },
        addItem(searchItem: universalSearchResultItem) {
            const category = searchItem.originalCategory ?? searchItem.category ?? "Other";

            Array.from(self.categoryToIds.keys()).forEach((existingCategory) => {
                const existingItems = self.categoryToIds.get(existingCategory) || [];

                if (existingItems.some((item) => item.id === searchItem.id)) {
                    self.categoryToIds.set(
                        existingCategory,
                        existingItems.filter((item) => item.id !== searchItem.id)
                    );
                }
            });

            const current = self.categoryToIds.get(category) ?? [];
            const iconCategory = searchItem.originalCategory ?? searchItem.category;
            const iconKey = typeof iconCategory === "string" ? iconCategory.toLowerCase() : "";
            const iconToUse: FluentIcon | undefined = searchItem.icon ? toJS(categoryIconMap[iconKey]) : toJS(Search24Filled);

            const newItem = SearchResultItemModel.create({
                id: searchItem.id,
                title: searchItem.title,
                description: searchItem.description,
                navigationTarget: searchItem.navigationTarget,
                icon: iconToUse,
                appPath: searchItem.appPath,
                category: searchItem.category,
                originalCategory: searchItem.originalCategory,
                timestamp: Date.now()
            });
            self.categoryToIds.set(category, [newItem, ...current]);
            self.version += 1;

            const allItems: { item: Instance<typeof SearchResultItemModel>; category: string }[] = [];

            Array.from(self.categoryToIds.keys()).forEach((cat) => {
                const items = self.categoryToIds.get(cat) || [];
                items.forEach((item) => {
                    allItems.push({ item, category: cat });
                });
            });

            if (allItems.length > maxHistoryCount) {
                allItems.sort((a, b) => a.item.timestamp - b.item.timestamp);

                const oldest = allItems[0];

                const oldestCategory = oldest ? oldest.category : "Recent Searches";
                const itemsInCategory = self.categoryToIds.get(oldestCategory) || [];
                const filteredItems = itemsInCategory.filter((item) => item.id !== oldest?.item.id);
                self.categoryToIds.set(oldestCategory, filteredItems);
            }
        },

        removeItem(category: string, itemId: string) {
            const currentItems = self.categoryToIds.get(category) || [];

            const originalLength = currentItems.length;

            const filteredItems = currentItems.filter((item) => item.id !== itemId);
            if (filteredItems.length < originalLength) {
                self.categoryToIds.set(category, filteredItems);
                self.version += 1;
            }
        }
    }));
